<ng-container *transloco="let convert;">
  @if (!waitingResponse && !successfulResponse) {
    <app-offer-modals-header role="dialog" aria-label="view-offer" [tenantName]="tenantName" heading="{{ convert('vehicles.counterOffer.textViewOffer') }}"
    [headingId]="modalId + '-title'"></app-offer-modals-header>
    <p class="text-danger fw-bold" [class.d-none]="!errorMessage" aria-atomic="true" aria-live="polite" #error>
      @if (errorMessage) {
        {{ convert('vehicles.counterOffer.errorMsgTitle') }}
      }
    </p>
    @if (showWarning) {
      <app-vehicle-changed-warning></app-vehicle-changed-warning>
    }
    <div class="modal__content">
      <app-offer-history [vin]="item.vin" [offerHistory]="offerHistory"
        [collapsible]="showDripBreakdown"></app-offer-history>
      <!--
        Today's disclosure block, now flag-off only (M4.10). Neither comp -- 10503:24898 Accept nor
        10503:24685 Counter -- renders anything between Offer History and the tab group; their only prose
        sits inside the active panel, beneath the comments field.

        textSelectCounter is not merely absent from the comps, it had become wrong here: "By selecting
        Counter, you will place a counter offer" names the single action this modal used to be about, and
        it now sits above a control offering three. The Selling Disclosures link is kept for every tenant
        NOT on the all-in layout rather than retired outright -- a legal link should not disappear
        because a comp omitted it -- and its absence from the drip panels is raised with the story owner.

        textTransportationDescription is unconditional here, exactly as it was before this epic: with the
        flag off there is no quoted transportation fee for it to follow. The all-in layout replaces it
        with the comp's action-oriented sentence, inside the panel, next to the figure it qualifies.
      -->
      @if (!showDripBreakdown) {
        <div id="disclosures">
          <p>
            {{ convert('vehicles.counterOffer.textSelectCounter') }}
          </p>
          @if (showSellingDisclosure) {
            <p>
              {{ convert('vehicles.counterOffer.textSelectAccept') }}
              <strong><a target="_blank" rel="opener" routerLink="{{ sellingDisclosureUrl }}" data-track-elem="button"
                data-track-name="Selling Disclosures"
                [attr.aria-label]="convert('vehicles.counterOffer.areaLabelSellingDisclosure')">{{
              convert('vehicles.counterOffer.textSelectDisclosure') }}</a></strong>.
              {{ textCounterOffer.textBindingAgreement }}
            </p>
          }
          <p data-test-id="app-initial-offer-e-modal-info-text">
            {{ textCounterOffer.textTransportationDescription }}
          </p>
        </div>
      }
      <form name="makeOfferForm" [formGroup]="form" novalidate>
        <!--
          All-in pricing, tabbed layout (M4.9), per Figma 10503:24898 (Accept) and 10503:24685 (Counter).

          One outcome at a time. The two-column layout in the @else below puts the seller's total and the
          buyer's counter total side by side, which is the comparison the epic exists to prevent; each tab
          shows only the breakdown, the disclosure and the button belonging to its own action.

          Written inline rather than pulled out into an ng-template like every other block in this file,
          and that is load-bearing: the panels outlet #CommentsField, whose formControlName resolves its
          ControlContainer through the template's DECLARATION hierarchy. An #OfferActionTabs template
          declared beside the others at the bottom of this file is not lexically inside the form, so
          #CommentsField -- which has to be declared inside it -- is not in scope there. The outlet then
          resolves to nothing and ngTemplateOutlet renders an unresolved ref as empty, silently, with no
          error anywhere. Keeping this markup in the form keeps both in one scope.

          The panels stay in the DOM while inactive and are hidden with [hidden] rather than removed, so
          every tab's aria-controls resolves to a real element. Their CONTENT is behind @if, so the shared
          comments field and the currency input exist exactly once -- two live formControlName bindings on
          one control is how a typed counter ends up submitted as a different number. Switching tabs
          therefore tears the field down and rebuilds it, but the control lives on the form group rather
          than on the directive, so the amount the buyer typed survives the round trip.
        -->
        @if (showDripBreakdown) {
          <div class="offer-actions">
            <div class="offer-actions__tablist" role="tablist" id="vom_offer_actions_tablist"
              [attr.aria-labelledby]="modalId + '-title'">
              <button #offerActionTab type="button" role="tab" id="vom_accept_tab" class="offer-actions__tab"
                [class.offer-actions__tab--active]="isActiveTab('accept')" [attr.aria-selected]="isActiveTab('accept')"
                [attr.tabindex]="isActiveTab('accept') ? 0 : -1" aria-controls="vom_accept_panel"
                data-track-elem="tab" data-track-name="Accept" data-track-trigger="switch"
                (click)="selectTab('accept')" (keydown)="onTabKeydown($event)">
                {{ convert('vehicles.counterOffer.btnAccept') }}
              </button>
              <button #offerActionTab type="button" role="tab" id="vom_counter_tab" class="offer-actions__tab"
                [class.offer-actions__tab--active]="isActiveTab('counter')" [attr.aria-selected]="isActiveTab('counter')"
                [attr.tabindex]="isActiveTab('counter') ? 0 : -1" aria-controls="vom_counter_panel"
                data-track-elem="tab" data-track-name="Counter" data-track-trigger="switch"
                (click)="selectTab('counter')" (keydown)="onTabKeydown($event)">
                {{ convert('vehicles.counterOffer.btnCounter') }}
              </button>
              <button #offerActionTab type="button" role="tab" id="vom_decline_tab" class="offer-actions__tab"
                [class.offer-actions__tab--active]="isActiveTab('decline')" [attr.aria-selected]="isActiveTab('decline')"
                [attr.tabindex]="isActiveTab('decline') ? 0 : -1" aria-controls="vom_decline_panel"
                data-track-elem="tab" data-track-name="Decline" data-track-trigger="switch"
                (click)="selectTab('decline')" (keydown)="onTabKeydown($event)">
                {{ convert('vehicles.counterOffer.btnDecline') }}
              </button>
            </div>

            <div id="vom_accept_panel" class="offer-actions__panel" role="tabpanel" aria-labelledby="vom_accept_tab"
              [hidden]="!isActiveTab('accept')">
              @if (isActiveTab('accept')) {
                <!--
                  The seller's offer as the base, with the fees added beneath it. Pattern B, not the
                  DECOMPOSED headline M4.5 shipped: the comp labels the seller's actual number 'SELLER
                  OFFER' rather than relabelling it 'VEHICLE PRICE' under an all-in headline the seller
                  never quoted. Same total.
                -->
                <ng-container *ngTemplateOutlet="SellerOfferBreakdown"></ng-container>
                <ng-container *ngTemplateOutlet="CommentsField; context: { convert: convert }"></ng-container>
                <p class="offer-actions__disclosure" id="vom_accept_binding_text"
                  [innerHTML]="convert('vehicles.counterOffer.msgAcceptBindingAllIn', { amount: acceptQuotedPriceDisplay })"></p>
                <!--
                  The sentence that makes the inclusive figure above accurate rather than overstated: it
                  names transportation as optional and Cancel as the way out. It is why the binding
                  sentence can quote the total instead of the all-in amount, so the two belong together --
                  do not keep one without the other.
                -->
                @if (showTransportationDisclosure) {
                  <p class="offer-actions__disclosure" id="vom_accept_transportation_text"
                    [innerHTML]="convert('vehicles.counterOffer.msgTransportationOptional')"></p>
                }
                <ng-container *ngTemplateOutlet="ActionError; context: { convert: convert, columnClass: '' }"></ng-container>
                <div class="offer-actions__buttons d-flex flex-column flex-lg-row lg-justify-content-start gap-3">
                  <button class="btn btn-primary" (click)="accept()" type="button" data-track-elem="button"
                    allytmln="acceptBuyAction" id="vom_accept_offer_button">
                    {{ convert('vehicles.counterOffer.btnAccept') }}
                  </button>
                  <ng-container *ngTemplateOutlet="CancelAction; context: { convert: convert }"></ng-container>
                </div>
              }
            </div>

            <div id="vom_counter_panel" class="offer-actions__panel" role="tabpanel" aria-labelledby="vom_counter_tab"
              [hidden]="!isActiveTab('counter')">
              @if (isActiveTab('counter')) {
                <!--
                  The seller's offer stays on screen as a single line rather than a second breakdown: the
                  buyer needs to know what they are countering, but two itemised totals in one panel is the
                  side-by-side comparison this layout exists to remove. The raw amount is what the seller
                  named.
                -->
                <ng-container *ngTemplateOutlet="SellerOfferAmount; context: { convert: convert }"></ng-container>
                <ng-container *ngTemplateOutlet="CounterAmountBreakdown"></ng-container>
                <ng-container *ngTemplateOutlet="ActionError; context: { convert: convert, columnClass: '' }"></ng-container>
                <ng-container *ngTemplateOutlet="CommentsField; context: { convert: convert }"></ng-container>
                <p class="offer-actions__disclosure" id="vom_counter_binding_text"
                  [innerHTML]="convert('vehicles.counterOffer.msgCounterBindingAllIn', { amount: counterQuotedPriceDisplay })"></p>
                @if (showTransportationDisclosure) {
                  <p class="offer-actions__disclosure" id="vom_counter_transportation_text"
                    [innerHTML]="convert('vehicles.counterOffer.msgTransportationOptional')"></p>
                }
                <div class="offer-actions__buttons d-flex flex-column flex-lg-row lg-justify-content-start gap-3">
                  <button id="vom_counter_offer_button" class="btn btn-primary" (click)="counter()" type="button"
                    data-track-elem="button" allytmln="counterBuyAction">
                    {{ convert('vehicles.counterOffer.btnCounter') }}
                  </button>
                  <ng-container *ngTemplateOutlet="CancelAction; context: { convert: convert }"></ng-container>
                </div>
              }
            </div>

            <div id="vom_decline_panel" class="offer-actions__panel" role="tabpanel" aria-labelledby="vom_decline_tab"
              [hidden]="!isActiveTab('decline')">
              @if (isActiveTab('decline')) {
                <!--
                  Still no breakdown and no binding sentence, and now that is the comp's position too rather
                  than only ours: declining creates no payment obligation, so there is no total to disclose
                  and nothing to be bound to. M4.10 recorded that no comp existed for this tab; one turned
                  up (EHI View Offer Decline), and all it adds is the sentence below.

                  The sentence is NOT gated on the pricing flag, because this whole tabbed layout is -- the
                  flag-off modal is the @else branch further down, which has no tabs and no decline panel.

                  The comp also drops the seller's offer line from this panel, leaving the header strip's
                  AMOUNT as the only figure. M4.10 kept it anyway and raised it; the story owner has since
                  ruled for the comp (2026-10-01), so it is gone and the comp and the code now agree.

                  Within this tab set the line is the COUNTER panel's alone. That is where the buyer is
                  naming a number against the seller's, so the seller's has to be in front of them. Accept
                  also shows the figure, but as its breakdown's base row (labelSeller) rather than as this
                  standalone line, so it needs no change here. Declining is still not done blind: the header
                  strip's AMOUNT sits above all three panels regardless of which tab is open.
                -->
                <ng-container *ngTemplateOutlet="CommentsField; context: { convert: convert }"></ng-container>
                <p class="offer-actions__disclosure" id="vom_decline_text"
                  [innerHTML]="convert('vehicles.counterOffer.msgDeclineAllIn')"></p>
                <ng-container *ngTemplateOutlet="ActionError; context: { convert: convert, columnClass: '' }"></ng-container>
                <div class="offer-actions__buttons d-flex flex-column flex-lg-row lg-justify-content-start gap-3">
                  <button class="btn btn-primary" type="button" (click)="decline()" data-track-elem="button"
                    allytmln="declineBuyAction" id="vom_decline_offer_button">
                    {{ convert('vehicles.counterOffer.btnDecline') }}
                  </button>
                  <ng-container *ngTemplateOutlet="CancelAction; context: { convert: convert }"></ng-container>
                </div>
              }
            </div>
          </div>
        } @else {
          <ng-container *ngTemplateOutlet="CommentsField; context: { convert: convert }"></ng-container>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group service-fee-details">
                <!--Show just offer amount-->
                @if (!showBuyFeesInOffers()) {
                  <app-datalist-item identifier="auction_info_service_fee_text"
                    label="{{ convert('vehicles.counterOffer.labelSeller') }}"
                    value="{{ formatCurrency(rootOffer.amount, false) }}">
                  </app-datalist-item>
                } @else {
                  <app-datalist-item identifier="auction_info_service_fee_text"
                    label="{{ convert('vehicles.counterOffer.labelSeller') }}"
                    value="{{ formatCurrency(rootOffer.amount, false) }}">
                  </app-datalist-item>
                  <app-datalist-item identifier="auction_info_service_fee_text"
                    label="{{ convert('vehicles.counterOffer.fees') }}"
                    value="{{ formatCurrency(bidItemBuyFeeTotal, true) }}">
                    <span>
                      @switch (isMobile) {
                        @case (true) {
                          <span>
                            <app-tooltip [tooltip]="feeDetailsTooltip" [showToolTipAsHtml]="true"
                              triggers="click :keydown.escape :keydown.tab" (click)="analyticsInfo('Service Fee Details')"
                              allytmln="Service Fee Details">
                              <i class="allycon-help-outline text-primary do-not-print" aria-hidden="true"></i>
                            </app-tooltip>
                          </span>
                        }
                        @case (false) {
                          <span>
                            <app-tooltip [tooltip]="feeDetailsTooltip" [showToolTipAsHtml]="true"
                              triggers="hover click :keydown.escape :keydown.tab" (click)="analyticsInfo('Service Fee Details')"
                              allytmln="Service Fee Details">
                              <i class="allycon-help-outline text-primary do-not-print" aria-hidden="true"></i>
                            </app-tooltip>
                          </span>
                        }
                        @default {
                          <span>
                            <app-tooltip [tooltip]="feeDetailsTooltip" [showToolTipAsHtml]="true"
                              triggers="hover click :keydown.escape :keydown.tab"
                              (click)="analyticsInfo('Service Fee Details')" allytmln="Service Fee Details">
                              <i class="allycon-help-outline text-primary do-not-print" aria-hidden="true"></i>
                            </app-tooltip>
                          </span>
                        }
                      }
                    </span>
                  </app-datalist-item>
                }
                <!-- Show offer amounts and buy fees-->
              </div>
              <div class="d-flex flex-column flex-lg-row lg-justify-content-start gap-3">
                <button class="btn btn-primary" (click)="accept()" type="button" data-track-elem="button"
                  allytmln="acceptBuyAction" id="vom_accept_offer_button">
                  {{ convert('vehicles.counterOffer.btnAccept') }}
                </button>
                <button class="ms-lg-2 mt-3 mt-lg-0 btn btn-secondary" type="button" (click)="decline()"
                  data-track-elem="button" allytmln="declineBuyAction" id="vom_decline_offer_button">
                  {{ convert('vehicles.counterOffer.btnDecline') }}
                </button>
              </div>
            </div>
            <div class="col-md-6 counter-offer">
              <div class="form-group">
                <ng-container *ngTemplateOutlet="CounterAmount; context: { convert: convert }"></ng-container>
              </div>
              <div class="d-flex flex-column flex-lg-row lg-justify-content-start">
                <button id="vom_counter_offer_button" class="btn btn-primary" (click)="counter()" type="button"
                  data-track-elem="button" allytmln="counterBuyAction">
                  {{ convert('vehicles.counterOffer.btnCounter') }}
                </button>
              </div>
            </div>
            <ng-container *ngTemplateOutlet="ActionError; context: { convert: convert, columnClass: 'col-md-12' }"></ng-container>
          </div>
        }

        <!--
          Declared INSIDE the form on purpose, and it will not work anywhere else.

          formControlName resolves its ControlContainer through the node injector, which walks the
          template's DECLARATION hierarchy rather than the outlet's, so a copy of this block declared
          beside the other ng-templates below throws NG01050 even though the outlet rendering it sits
          inside the form. The currency-input templates below can live outside because they are handed
          the form group as an explicit [inputForm] input instead of resolving it through DI.

          The textarea, its character count and its label are shared rather than copied per panel because
          accept(), counter() and decline() all submit offerComment: three copies is three chances for one
          of them to bind a different control name and silently drop what the buyer wrote. Only one panel's
          content is instantiated at a time, so the control has exactly one live binding.
        -->
        <ng-template #CommentsField let-convert="convert">
          <div class="form-group">
            <label class="d-block text-start" for="counter-offer-comment">{{ convert('vehicles.counterOffer.textComments')
            }}</label>
            <textarea id="vom_counter_offer_comment" class="form-control" rows="3" maxlength="150"
              formControlName="offerComment" (keyup)="replaceInvalidCharacters($event)" aria-label="Comments" appCharacterCount
            #comment="appCharacterCount"></textarea>
            <app-character-count [count]="comment.count$ | async" [maxLength]="150"
              [characterCountTranslate]="characterCountData">
            </app-character-count>
          </div>
        </ng-template>
      </form>
    </div>
    <app-modal-close (close)="cancel()" [identifier]="'vom_close_x_button'"></app-modal-close>
  }

  <!-- Loading states:-->
  @if (isSubmittingOffer()) {
    <!-- submitting  offer/counter offer -->
    <app-action-submitting actionText="{{ convert('vehicles.counterOffer.actionTextCounteringOffer') }}">
    </app-action-submitting>
  }

  @if (isSubmittingDecline()) {
    <!-- submitting decline -->
    <app-action-submitting actionText="{{ convert('vehicles.counterOffer.actionTextDecliningOffer') }}">
    </app-action-submitting>
  }

  @if (isSubmittingAccept()) {
    <!-- submitting accept -->
    <app-action-submitting actionText="{{ convert('vehicles.counterOffer.actionTextAcceptingOffer') }}">
    </app-action-submitting>
  }

  <!-- Accepted states:-->

  @if (isCounterAccepted()) {
    <!-- Countered Offer-->
    <app-action-submitted (close)="cancel()"
      actionText="{{ convert('vehicles.counterOffer.actionTextOfferCountered') }}" [tenantName]="tenantName">
    </app-action-submitted>
  }

  @if (isOfferDeclined()) {
    <!--  Declined Offer-->
    <app-action-submitted (close)="cancel()" actionText="{{ convert('vehicles.counterOffer.actionTextOfferDeclined') }}"
    [tenantName]="tenantName"></app-action-submitted>
  }

  @if (isOfferAccepted()) {
    <!--  Accepted Offer-->
    <app-action-submitted (close)="cancel()" actionText="{{ convert('vehicles.counterOffer.actionTextOfferAccepted') }}"
    [tenantName]="tenantName"></app-action-submitted>
  }

  <!--
    The seller's offer as a plain labelled line, for the panels that do not itemise it. labelSeller is
    the same key the flag-off datalist item uses, so the two layouts name this figure identically.
  -->
  <ng-template #SellerOfferAmount let-convert="convert">
    <app-datalist-item identifier="vom_seller_offer_amount"
      label="{{ convert('vehicles.counterOffer.labelSeller') }}"
      value="{{ formatCurrency(rootOffer.amount, false) }}">
    </app-datalist-item>
  </ng-template>

  <!--
    Cancel, per the comps: every panel offers a way out that is not one of the three bindings. The X in
    the header does the same thing, and both route through cancel() rather than duplicating the emit.
  -->
  <ng-template #CancelAction let-convert="convert">
    <button id="vom_cancel_button" class="ms-lg-2 mt-3 mt-lg-0 btn btn-link" type="button" (click)="cancel()"
      data-track-elem="button" data-track-name="Cancel" data-track-trigger="close"
      data-test-id="app-counter-offer-e-cancel-button">
      {{ convert('common.btnCancel') }}
    </button>
  </ng-template>

  <!--
    The error line. One live region either way, always in the DOM so a message announces when it arrives
    rather than when the region does. columnClass carries the grid class the two-column layout needs and
    is empty in the tabbed one, which is not a grid.

    In the tabbed layout it is drawn inside the active panel -- on Counter directly under the breakdown
    that holds the amount field, elsewhere above the buttons -- and selectTab clears it. It used to sit
    beneath all three panels, so an empty-counter refusal sat away from its field and stayed on screen
    over the Decline tab. Only the active panel is rendered, so the id stays unique.
  -->
  <ng-template #ActionError let-convert="convert" let-columnClass="columnClass">
    <div class="error_message {{ columnClass }}" aria-atomic="true" aria-live="polite">
      @if (errorMessage) {
        <p class="text-danger fw-bold" id="counter-offer-amount-error" #error role="alert">
          {{errorMessage === BuyErrors?.MSG_BUY_003 ? errorMessage : convert('error.' + errorMessage)}}
        </p>
      }
    </div>
  </ng-template>

  <!--
    All-in pricing (M4.5, re-patterned in M4.9), Pattern B. baseAmount is the RAW seller offer: the
    breakdown adds the fee rows itself to reach the total, so binding dripSellerAllInAmount here would
    double-count every one of them.

    Whole dollars per the comp. This pairs with acceptQuotedPriceDisplay, which formats the same total
    for the sentence beneath: flip one without the other and the prose and this TOTAL row quote
    different numbers for the same obligation.
  -->
  <ng-template #SellerOfferBreakdown>
    <app-modal-price-breakdown
      [idPrefix]="dripIdPrefix"
      [pattern]="additivePattern"
      [baseAmount]="dripSellerBaseAmount"
      [feeRows]="dripFeeRows"
      [transportationFee]="dripTransportationFee"
      [translations]="dripSellerBreakdownTranslations"
      [showCents]="false">
    </app-modal-price-breakdown>
  </ng-template>

  <!--
    The currency field itself, with no label of its own, so the two hosts below can each supply the
    labelling their layout needs. Extracted rather than copied: it is the only place the buyer names
    the amount their counter binds them to, and two copies drifting -- a mask, a separator, an id --
    is a money defect. counterAmountInputConfig carries whichever labelledby the rendered layout has.
  -->
  <ng-template #CounterAmountInput>
    <app-currency-input
      [inputConfig]="counterAmountInputConfig"
      [inputForm]="form"
      [inputId]="'vom_counter_offer_textbox'">
    </app-currency-input>
  </ng-template>

  <!--
    Today's amount block: its own label above the field. Used whenever the all-in breakdown is not
    rendering, which is every flag-off case.
  -->
  <ng-template #CounterAmount let-convert="convert">
    <label class="d-block text-start text-uppercase" for="counter-offer-amount" id="counter-offer-amount-label">{{
    convert('vehicles.counterOffer.btnCounter') }}</label>
    <ng-container *ngTemplateOutlet="CounterAmountInput"></ng-container>
  </ng-template>

  <!--
    All-in pricing (M4.5), Pattern B. The field moves inside the breakdown, labelled by the
    breakdown's own base-amount label -- which is why the <label> above is not rendered here too --
    with the required fees and optional transportation itemised beneath it and the total updating as
    the buyer types.

    The projected wrapper must carry appModalPriceBreakdownAmount: content matching no ng-content
    selector is discarded silently rather than rendered, so the field would simply vanish without it.
  -->
  <ng-template #CounterAmountBreakdown>
    <app-modal-price-breakdown
      [idPrefix]="dripCounterIdPrefix"
      [pattern]="additivePattern"
      [baseAmount]="dripCounterBaseAmount"
      [feeRows]="dripFeeRows"
      [transportationFee]="dripTransportationFee"
      [translations]="dripCounterBreakdownTranslations"
      [useProjectedAmount]="true"
      [inlineControlOnNarrow]="true"
      [showCents]="false">
      <span appModalPriceBreakdownAmount class="counter-offer-breakdown-control">
        <ng-container *ngTemplateOutlet="CounterAmountInput"></ng-container>
      </span>
    </app-modal-price-breakdown>
  </ng-template>
</ng-container>
